<template>
  <div class="card">
    <div class="card-body p-0">
      <div class="event-explore-map-container">
        <div id="event-explore-map" class="event-explore-map"></div>
        <div v-if="isLoading" class="map-loading-overlay">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import _ from 'lodash';

export default {
  props: {
    races: {
      type: Array,
      default: () => []
    },
    mapView: {
      type: Object,
      default: null
    },
    selectedRaceId: {
      type: Number,
      default: null
    },
    hoveredRaceId: {
      type: Number,
      default: null
    },
    useMetric: {
      type: Boolean,
      default: true
    },
    mapboxToken: {
      type: String,
      required: true
    }
  },

  beforeCreate() {
    // Mapbox GL map instance must NOT be reactive — Vue's reactivity proxy
    // breaks mapbox-gl v3's internal WebGL rendering.
    this.map = null;
  },

  data() {
    return {
      isLoading: false,
      currentPopup: null,
      markersLayer: 'race-markers',
      markersSource: 'race-markers',
      pendingBounds: null
    };
  },

  mounted() {
    this.initializeMap();
  },

  beforeUnmount() {
    if (this.map) {
      this.map.remove();
    }
  },

  watch: {
    races(newRaces) {
      this.updateMarkers(newRaces);
    },
    hoveredRaceId(newId, oldId) {
      this.updateHoverState(newId, oldId);
    },
    selectedRaceId(newId) {
      if (newId) {
        this.showPopupForRace(newId);
      }
    }
  },

  methods: {
    initializeMap() {
      mapboxgl.accessToken = this.mapboxToken;

      // Default to center of US
      const defaultCenter = [-95, 40];
      const defaultZoom = 4;

      const mapOptions = {
        container: 'event-explore-map',
        style: 'mapbox://styles/mapbox/streets-v11',
        zoom: defaultZoom,
        center: defaultCenter
      };

      // Check if we have a valid user map view (handles both lat/lng and latitude/longitude)
      console.log('[EventExploreMap] mapView prop:', this.mapView);
      const userMapView = this.parseUserMapView();
      console.log('[EventExploreMap] parseUserMapView result:', userMapView);

      if (userMapView) {
        // User has a saved map view - use it
        if (userMapView.bounds) {
          // Will fit to bounds after map loads
          // Convert to plain array to avoid Vue reactivity issues with Mapbox
          this.pendingBounds = [
            [userMapView.bounds[0][0], userMapView.bounds[0][1]],
            [userMapView.bounds[1][0], userMapView.bounds[1][1]]
          ];
          console.log('[EventExploreMap] Set pendingBounds:', this.pendingBounds);
        } else if (userMapView.center) {
          mapOptions.center = userMapView.center;
          mapOptions.zoom = userMapView.zoom || 8;
        }
      }

      this.map = new mapboxgl.Map(mapOptions);

      // Add navigation controls
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Add geocoder control
      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        marker: false,
        placeholder: 'Search location...'
      });
      this.map.addControl(geocoder, 'top-left');

      // Add geolocate control for browser-based location
      const geolocateControl = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: false
        },
        trackUserLocation: false,
        showUserHeading: false,
        showAccuracyCircle: false
      });
      this.map.addControl(geolocateControl, 'top-right');

      // Store for use in load handler
      const shouldRequestGeolocation = !userMapView;

      // Listen for geolocation result (always register, may not be triggered)
      geolocateControl.on('geolocate', (e) => {
        // User granted permission - zoom to their location
        this.map.flyTo({
          center: [e.coords.longitude, e.coords.latitude],
          zoom: 8
        });
      });

      // Wait for map to load - single handler for all load logic
      this.map.on('load', () => {
        console.log('[EventExploreMap] Map load event fired, pendingBounds:', this.pendingBounds);
        // Apply pending bounds if we have them (from user's saved map view)
        if (this.pendingBounds) {
          console.log('[EventExploreMap] Calling fitBounds with:', this.pendingBounds);
          this.map.fitBounds(this.pendingBounds, { padding: 50 });
          this.pendingBounds = null;
        } else if (shouldRequestGeolocation) {
          // No saved map view - try to get browser geolocation
          geolocateControl.trigger();
        }

        this.setupMarkerLayer();
        this.updateMarkers(this.races);

        // Emit initial bounds to trigger first race fetch
        this.emitCurrentBounds();
      });

      // Emit map bounds on movement
      this.map.on('moveend', this.handleMapMoveEnd);

      // Handle marker clicks
      this.map.on('click', this.markersLayer, (e) => {
        const feature = e.features[0];
        if (feature) {
          this.showPopup(feature, e.lngLat);
        }
      });

      // Change cursor on hover
      this.map.on('mouseenter', this.markersLayer, () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });

      this.map.on('mouseleave', this.markersLayer, () => {
        this.map.getCanvas().style.cursor = '';
      });

      // Emit hover events
      this.map.on('mouseenter', this.markersLayer, (e) => {
        const feature = e.features[0];
        if (feature) {
          this.$emit('race-hover', feature.properties.id);
        }
      });

      this.map.on('mouseleave', this.markersLayer, () => {
        this.$emit('race-hover', null);
      });
    },

    setupMarkerLayer() {
      // Add source for race markers
      this.map.addSource(this.markersSource, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        },
        generateId: false // Use feature IDs from the data
      });

      // Add layer for race markers
      this.map.addLayer({
        id: this.markersLayer,
        type: 'circle',
        source: this.markersSource,
        paint: {
          'circle-radius': [
            'case',
            ['boolean', ['feature-state', 'hovered'], false],
            10,
            ['boolean', ['feature-state', 'selected'], false],
            9,
            7
          ],
          'circle-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#FF6B6B',
            '#2E86C1'
          ],
          'circle-stroke-width': [
            'case',
            ['boolean', ['feature-state', 'hovered'], false],
            3,
            2
          ],
          'circle-stroke-color': '#FFFFFF',
          'circle-opacity': 0.9
        }
      });
    },

    updateMarkers(races) {
      if (!this.map || !this.map.getSource(this.markersSource)) return;

      // Filter races with valid coordinates
      const validRaces = races.filter(race => race.route && race.route.latitude && race.route.longitude);

      // Group races by event (using event name + location as key)
      const eventGroups = validRaces.reduce((groups, race) => {
        const eventKey = `${race.event.name}||${race.event.location}`;
        if (!groups[eventKey]) {
          groups[eventKey] = {
            eventName: race.event.name,
            location: race.event.location,
            latitudes: [],
            longitudes: [],
            races: []
          };
        }
        groups[eventKey].latitudes.push(race.route.latitude);
        groups[eventKey].longitudes.push(race.route.longitude);
        groups[eventKey].races.push({
          id: race.id,
          name: race.name,
          date: race.date,
          distance: race.route.distance,
          elevationGain: race.route.elevation_gain,
          path: race.path
        });
        return groups;
      }, {});

      // Create GeoJSON features for each event group
      const features = Object.entries(eventGroups).map(([eventKey, group], index) => {
        // Calculate average latitude and longitude
        const avgLat = group.latitudes.reduce((sum, lat) => sum + lat, 0) / group.latitudes.length;
        const avgLng = group.longitudes.reduce((sum, lng) => sum + lng, 0) / group.longitudes.length;

        return {
          type: 'Feature',
          id: index, // Use index as feature ID
          geometry: {
            type: 'Point',
            coordinates: [avgLng, avgLat]
          },
          properties: {
            eventKey: eventKey,
            eventName: group.eventName,
            location: group.location,
            races: JSON.stringify(group.races) // Stringify for GeoJSON compatibility
          }
        };
      });

      this.map.getSource(this.markersSource).setData({
        type: 'FeatureCollection',
        features: features
      });
    },

    handleMapMoveEnd: _.debounce(function() {
      this.emitCurrentBounds();
    }, 300),

    emitCurrentBounds() {
      if (!this.map) return;
      const bounds = this.map.getBounds();
      this.$emit('map-move', {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      });
    },

    showPopup(feature, lngLat) {
      if (this.currentPopup) {
        this.currentPopup.remove();
      }

      const props = feature.properties;
      const races = JSON.parse(props.races);

      // Build races list HTML
      const racesListHTML = races.map(race => {
        const distance = this.useMetric
          ? `${(race.distance / 1000).toFixed(1)} km`
          : `${(race.distance * 0.000621371).toFixed(1)} mi`;
        const elevation = this.useMetric
          ? `${Math.round(race.elevationGain)} m`
          : `${Math.round(race.elevationGain * 3.28084)} ft`;

        return `<li class="mb-1">${race.name}: ${distance} (${elevation})</li>`;
      }).join('');

      const popupContent = `
        <div class="event-popup">
          <h6 class="mb-1">
            <a href="${races[0].path}" target="_blank" rel="noopener noreferrer"
               style="color: #2E86C1; font-weight: bold; text-decoration: none;">
              ${props.eventName}
            </a>
          </h6>
          <p class="mb-2 small text-muted">${props.location}</p>
          <div class="races-list">
            <strong class="small">Races:</strong>
            <ul class="list-unstyled mb-0 small">
              ${racesListHTML}
            </ul>
          </div>
        </div>
      `;

      this.currentPopup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
        maxWidth: '300px'
      })
        .setLngLat(lngLat)
        .setHTML(popupContent)
        .addTo(this.map);
    },

    showPopupForRace(raceId) {
      const race = this.races.find(r => r.id === raceId);
      if (!race || !race.route) return;

      // Find all races from the same event
      const eventKey = `${race.event.name}||${race.event.location}`;
      const eventRaces = this.races.filter(r =>
        r.event.name === race.event.name &&
        r.event.location === race.event.location &&
        r.route && r.route.latitude && r.route.longitude
      );

      // Calculate average position for the event
      const avgLat = eventRaces.reduce((sum, r) => sum + r.route.latitude, 0) / eventRaces.length;
      const avgLng = eventRaces.reduce((sum, r) => sum + r.route.longitude, 0) / eventRaces.length;
      const lngLat = [avgLng, avgLat];

      // Build feature with all races from the event
      const feature = {
        properties: {
          eventKey: eventKey,
          eventName: race.event.name,
          location: race.event.location,
          races: JSON.stringify(eventRaces.map(r => ({
            id: r.id,
            name: r.name,
            date: r.date,
            distance: r.route.distance,
            elevationGain: r.route.elevation_gain,
            path: r.path
          })))
        }
      };

      this.showPopup(feature, lngLat);
      this.map.flyTo({ center: lngLat, zoom: 12 });
    },

    updateHoverState(newId, oldId) {
      if (!this.map || !this.map.getSource(this.markersSource)) return;

      if (oldId !== null && oldId !== undefined) {
        this.map.setFeatureState(
          {
            source: this.markersSource,
            id: oldId
          },
          { hovered: false }
        );
      }

      if (newId !== null && newId !== undefined) {
        this.map.setFeatureState(
          {
            source: this.markersSource,
            id: newId
          },
          { hovered: true }
        );
      }
    },

    /**
     * Parse the user's map view from props.
     * Handles both lat/lng and latitude/longitude field names.
     * Returns { center: [lng, lat], zoom } or { bounds: [[west, south], [east, north]] }
     * Returns null if no valid map view is available.
     */
    parseUserMapView() {
      if (!this.mapView) return null;

      const mv = this.mapView;

      // Check for bounds-based view (north, south, east, west)
      const hasBounds = typeof mv.north === 'number' &&
                        typeof mv.south === 'number' &&
                        typeof mv.east === 'number' &&
                        typeof mv.west === 'number' &&
                        !isNaN(mv.north) && !isNaN(mv.south) &&
                        !isNaN(mv.east) && !isNaN(mv.west);

      if (hasBounds) {
        return {
          bounds: [[mv.west, mv.south], [mv.east, mv.north]]
        };
      }

      // Check for center-based view (supports both lat/lng and latitude/longitude)
      const lng = typeof mv.lng === 'number' ? mv.lng : mv.longitude;
      const lat = typeof mv.lat === 'number' ? mv.lat : mv.latitude;
      const zoom = mv.zoom;

      const hasCenter = typeof lng === 'number' &&
                        typeof lat === 'number' &&
                        !isNaN(lng) && !isNaN(lat);

      if (hasCenter) {
        return {
          center: [lng, lat],
          zoom: typeof zoom === 'number' && !isNaN(zoom) ? zoom : 8
        };
      }

      return null;
    }
  }
};
</script>

<style>
/* Critical height styles - not scoped to ensure mapbox container renders properly */
.event-explore-map-container {
  position: relative;
  height: 600px !important;
  min-height: 500px !important;
  width: 100%;
}

@media (min-width: 768px) {
  .event-explore-map-container {
    height: calc(100vh - 250px) !important;
  }
}

#event-explore-map,
#event-explore-map .mapboxgl-map,
#event-explore-map .mapboxgl-canvas-container,
#event-explore-map .mapboxgl-canvas {
  height: 100% !important;
  width: 100% !important;
}

#event-explore-map {
  background: #e9ecef;
}

.event-explore-map-container .map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
</style>

<style>
/* Global styles for Mapbox popups */
.race-popup h6,
.event-popup h6 {
  color: #333;
  margin-bottom: 0.5rem;
}

.race-popup h6 a:hover,
.event-popup h6 a:hover {
  text-decoration: underline !important;
}

.race-popup p {
  margin-bottom: 0.25rem;
  color: #666;
}

.event-popup .races-list ul {
  margin-top: 0.25rem;
  padding-left: 1rem;
}

.event-popup .races-list li {
  color: #666;
  line-height: 1.4;
}

.mapboxgl-popup-content {
  padding: 12px;
  max-width: 300px;
}
</style>
